import React, { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, IconButton, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { alpha } from "@mui/material/styles";

// The description is opened from the info button in the top-right corner. It used
// to appear on its own after a hover delay, which fired while the user was just
// reading the chart.
const WidgetCard = ({ title, description, children, height = "100%" }) => {
  const [showInfo, setShowInfo] = useState(false);

  const toggleInfo = useCallback(() => setShowInfo((open) => !open), []);
  const closeInfo = useCallback(() => setShowInfo(false), []);

  // Escape closes the description, as it does for the dialogs elsewhere.
  useEffect(() => {
    if (!showInfo) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowInfo(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showInfo]);

  return (
    <Card
      sx={{
        height,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          p: 2,
          "&:last-child": { pb: 2 },
          transition: "filter 0.25s ease",
          filter: showInfo ? "blur(4px)" : "none",
        }}
      >
        {title && (
          // Keep long titles clear of the info button.
          <Typography variant="h6" sx={{ mb: 1.5, pr: description ? 4 : 0 }}>
            {title}
          </Typography>
        )}
        {children}
      </CardContent>

      {description && (
        <>
          <IconButton
            className="widget-info-button"
            onClick={toggleInfo}
            // The dashboard grid starts a drag on mousedown; without this the drag
            // swallows the click and the description never opens.
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            size="small"
            aria-label={showInfo ? "Hide description" : "Show description"}
            aria-expanded={showInfo}
            // Sits outside CardContent so the blur does not reach it, and above the
            // overlay so a second click closes the description.
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              color: "text.secondary",
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>

          <Box
            className="widget-info-button"
            onClick={closeInfo}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
              textAlign: "center",
              // Only swallow clicks while open, so the chart stays usable when closed.
              pointerEvents: showInfo ? "auto" : "none",
              cursor: showInfo ? "pointer" : "default",
              opacity: showInfo ? 1 : 0,
              transition: "opacity 0.25s ease",
              bgcolor: (t) => alpha(t.palette.background.paper, 0.55),
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.primary", fontWeight: 500, maxWidth: 360, lineHeight: 1.5 }}
            >
              {description}
            </Typography>
          </Box>
        </>
      )}
    </Card>
  );
};

export default WidgetCard;
